import { useCallback, useEffect, useRef } from 'react';

import { ReactPlugin, useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { ENV_VARS } from 'app/config/environment-vars';
import { useSubscriptionTechnicalName } from 'state/appState/appStateHooks';

export type ContextTypes = 'Project' | 'Template' | 'Subscription' | 'Common';

/**
 * Track an event from something that runs outside of react
 * @param {string} eventName the name of the event
 * @param {ContextTypes} context the context for the event
 * @param { { [key: string]: any }} properties the custom properties for the event
 */
export const trackEvent = (eventName: string, context: ContextTypes, properties?: Record<string, unknown>) => {
  const customProperties = properties === undefined ? { context: context } : { ...properties, context: context };

  pendo.track(eventName, customProperties);

  const plugin = getInsightsReactPlugin();
  plugin.getAppInsights().trackEvent({ name: eventName }, customProperties);
};

export const useSimpleEvent = (
  eventName: string,
  properties?: { [key: string]: string | number | boolean | string[] | null }
) => {
  const context = useAppInsightsContext();

  const propRef = useRef(properties);

  const tracker = useTrackEvent(context, eventName, properties);

  return useCallback(
    (properties?: { [key: string]: any }) => {
      // application insights tracking
      if (propRef.current !== undefined) {
        tracker({ ...propRef.current, ...properties });
      } else {
        tracker(properties);
      }
      // pendo tracking
      pendo.track(eventName, { ...properties });
    },
    [tracker, eventName]
  );
};

export const useTrackedPage = (
  signature: string,
  pageName: string,
  customProperties?: {
    [key: string]: any;
  }
) => {
  const context = useAppInsightsContext();
  const subscriptionName = useSubscriptionTechnicalName();

  useEffect(() => {
    // application insights tracking only
    context.getAppInsights().startTrackPage(pageName);
    return () => {
      const propertiesToSend =
        customProperties !== undefined
          ? { ...customProperties, tenant: subscriptionName }
          : { tenant: subscriptionName };

      context.getAppInsights().stopTrackPage(pageName, undefined, propertiesToSend);
    };
  }, [signature]);
};

let reactPlugin: ReactPlugin;
let appInsights: ApplicationInsights;

export function setupInsights(context?: { email?: string; subscriptionName?: string }) {
  if (reactPlugin && appInsights) {
    return { appInsights, reactPlugin };
  }

  reactPlugin = new ReactPlugin();
  appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: ENV_VARS.INSTRUMENTATION_KEY,
      enableAutoRouteTracking: false,
      extensions: [reactPlugin]
    }
  });
  appInsights.loadAppInsights();

  if (context?.email) {
    const validEmail = context.email.replace(/[,;=| ]+/g, '_');
    appInsights.context.user.id = validEmail;
    appInsights.context.user.authenticatedId = validEmail;
  }

  if (context?.subscriptionName) {
    appInsights.context.user.accountId = context.subscriptionName;
  }

  // fix an issue where setAuthenticatedUserContext does not work
  // see: https://github.com/Microsoft/ApplicationInsights-JS/issues/571
  appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
    const tags = envelope.tags;
    if (!tags) {
      return;
    }

    tags['ai.userId'] = appInsights.context.user.id;
    tags['ai.user.authUserId'] = appInsights.context.user.authenticatedId;
    tags['ai.user.accountId'] = appInsights.context.user.accountId;
  });

  return { appInsights, reactPlugin };
}

export function getAppInsights() {
  if (!appInsights) {
    throw new Error('Cannot return appInsights because it has not been initialized, call setupInsights first.');
  }
  return appInsights;
}

export function getInsightsReactPlugin() {
  if (!reactPlugin) {
    throw new Error('Cannot return reactPlugin because it has not been initialized, call setupInsights first.');
  }
  return reactPlugin;
}
